/**
 * Page-specific content registry for Resources detail pages.
 *
 * Only pages with an entry here render real sections; any Resources page
 * without an entry keeps the shared "pending" fallback in DetailPage.tsx.
 * Nothing here invents downloadable files, certifications or claims — the
 * Company Profile points the full document to a request rather than a file.
 */

import type { RouteKey } from "@/lib/routes";

export interface ResourceSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
}

export interface ResourcePageContent {
  sections: ResourceSection[];
  note?: string;
}

// ─── TRADE DOCUMENTS ───────────────────────────────────────────────
const TRADE_EN: ResourcePageContent = {
  sections: [
    {
      heading: "Overview",
      paragraphs: [
        "In physical commodity trade, each shipment is supported by a standard set of commercial and shipping documents. The list below shows the document types typically involved in transactions coordinated by BNM S.r.l. The exact set depends on the commodity, route, Incoterms basis and destination-country requirements.",
      ],
    },
    {
      heading: "Standard document set",
      items: [
        "Commercial invoice",
        "Sales contract (GAFTA / FOSFA / NAEGA or industry-standard, as applicable)",
        "Bill of lading or other transport document",
        "Certificate of origin",
        "Quality and weight certificates (independent surveyor, e.g. SGS, Bureau Veritas or equivalent)",
        "Phytosanitary or health certificates (where required by the commodity)",
        "Packing list",
        "Insurance certificate (where applicable)",
        "Payment documents (Documentary Letter of Credit, CAD or agreed instrument)",
      ],
    },
  ],
  note: "Document requirements are transaction-specific. This page is descriptive and does not constitute an offer. For the document set applicable to a particular trade, please contact us.",
};

const TRADE_IT: ResourcePageContent = {
  sections: [
    {
      heading: "Panoramica",
      paragraphs: [
        "Nel commercio fisico di commodities, ogni spedizione è accompagnata da un set standard di documenti commerciali e di spedizione. L'elenco seguente indica i tipi di documento tipicamente coinvolti nelle transazioni coordinate da BNM S.r.l. Il set esatto dipende dalla commodity, dalla rotta, dalla base Incoterms e dai requisiti del paese di destinazione.",
      ],
    },
    {
      heading: "Set documentale standard",
      items: [
        "Fattura commerciale",
        "Contratto di vendita (GAFTA / FOSFA / NAEGA o standard di settore, ove applicabile)",
        "Polizza di carico o altro documento di trasporto",
        "Certificato di origine",
        "Certificati di qualità e peso (surveyor indipendente, es. SGS, Bureau Veritas o equivalente)",
        "Certificati fitosanitari o sanitari (ove richiesti dalla commodity)",
        "Distinta di imballaggio (packing list)",
        "Certificato di assicurazione (ove applicabile)",
        "Documenti di pagamento (Lettera di Credito documentaria, CAD o strumento concordato)",
      ],
    },
  ],
  note: "I requisiti documentali sono specifici per ciascuna transazione. Questa pagina è descrittiva e non costituisce un'offerta. Per il set documentale applicabile a una specifica operazione, contattaci.",
};

// ─── COMPANY PROFILE ───────────────────────────────────────────────
const PROFILE_EN: ResourcePageContent = {
  sections: [
    {
      heading: "Overview",
      paragraphs: [
        "BNM S.R.L. is an Italian international trading company active in international trade, import and export, global sourcing, commodity supply and commercial consulting, across three sectors: grains & cereals, food products and petrochemical products.",
      ],
    },
    {
      heading: "At a glance",
      paragraphs: [
        "Registered in Italy with the Camera di Commercio Toscana Nord-Ovest (REA PI-258592), VAT 06654120481, share capital €10,000.00 fully paid. Full corporate details are published under Corporate Information.",
      ],
    },
    {
      heading: "Full profile",
      paragraphs: [
        "A complete company profile is available to qualified counterparties on request. Please contact us to obtain it.",
      ],
    },
  ],
};

const PROFILE_IT: ResourcePageContent = {
  sections: [
    {
      heading: "Panoramica",
      paragraphs: [
        "BNM S.R.L. è una società italiana di trading internazionale attiva nel commercio internazionale, nell'import ed export, nel global sourcing, nella fornitura di commodities e nella consulenza commerciale, in tre settori: cereali, prodotti alimentari e prodotti petrolchimici.",
      ],
    },
    {
      heading: "In sintesi",
      paragraphs: [
        "Registrata in Italia presso la Camera di Commercio Toscana Nord-Ovest (REA PI-258592), partita IVA 06654120481, capitale sociale € 10.000,00 interamente versato. I dati societari completi sono pubblicati nella pagina Informazioni Societarie.",
      ],
    },
    {
      heading: "Profilo completo",
      paragraphs: [
        "Un profilo aziendale completo è disponibile su richiesta per le controparti qualificate. Contattaci per ottenerlo.",
      ],
    },
  ],
};

// ─── REGISTRY ──────────────────────────────────────────────────────
// BNM Group Georgia: Resources detail pages kept on the pending fallback
// until Georgia-specific content and verified company data are provided.
// The Italy drafts above are retained but intentionally NOT wired.
export const RESOURCE_CONTENT: Partial<
  Record<RouteKey, { en: ResourcePageContent; ka: ResourcePageContent }>
> = {};
